import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ConfigStateService, LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { Popover, PopoverModule } from 'primeng/popover';
import { ActionMenuComponent } from 'src/app/shared/components/action-menu/action-menu.component';
import { ActionMenuItem } from 'src/app/shared/components/action-menu/models/action-menu-item.model';
import { BaseListComponent } from 'src/app/shared/components/base-list/base-list.component';
import { BaseListColumn } from 'src/app/shared/components/base-list/models/base-list-column.model';
import { BaseListColumnTemplateContext } from 'src/app/shared/components/base-list/models/base-list-column-template-context.model';
import { BaseListConfig } from 'src/app/shared/components/base-list/models/base-list-config.model';
import { CrudService } from 'src/core/services/crud/crud.service';
import { FreeSwitchInstanceDto } from '../../models/free-switch-instance.dto';
import { TenantLookupDto } from '../../models/tenant-lookup.dto';

type InstanceTplContext = BaseListColumnTemplateContext<FreeSwitchInstanceDto>;

/** Mirrors AgenticIdentityUserConsts.AdminUserName on the backend. */
const ADMIN_USER_NAME = 'admin';

@Component({
  selector: 'app-freeswitch-integration-list',
  standalone: true,
  imports: [CommonModule, LocalizationPipe, PopoverModule, ActionMenuComponent, BaseListComponent],
  templateUrl: './freeswitch-integration-list.component.html',
  styleUrls: ['./freeswitch-integration-list.component.scss'],
})
export class FreeSwitchIntegrationListComponent implements OnInit, AfterViewInit {
  private readonly localization = inject(LocalizationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly crudService = inject(CrudService);
  private readonly configState = inject(ConfigStateService);

  /**
   * The console is restricted to the host's `admin` user, enforced server-side by
   * `FreeSwitchInstanceAppService.CheckHostAdministrator`. Mirrored here only so the action is not
   * offered to someone the endpoint would answer with a 403 — this is not the access control itself.
   */
  private readonly isHostAdmin =
    this.configState.getDeep('currentUser.tenantId') == null &&
    (this.configState.getDeep('currentUser.userName') as string | undefined)?.toLowerCase() ===
      ADMIN_USER_NAME;

  @ViewChild('tenantPopover') tenantPopoverRef!: Popover;
  @ViewChild('nameTpl') nameTpl!: TemplateRef<InstanceTplContext>;
  @ViewChild('tenantsTpl') tenantsTpl!: TemplateRef<InstanceTplContext>;
  @ViewChild('actionsTpl') actionsTpl!: TemplateRef<InstanceTplContext>;

  @Input() instances: FreeSwitchInstanceDto[] = [];
  @Input() loading = false;
  @Input() canDelete = false;

  @Output() edit = new EventEmitter<FreeSwitchInstanceDto>();
  @Output() delete = new EventEmitter<FreeSwitchInstanceDto>();
  @Output() console = new EventEmitter<FreeSwitchInstanceDto>();

  listConfig!: BaseListConfig<FreeSwitchInstanceDto>;
  tenantPopoverInstance: FreeSwitchInstanceDto | null = null;

  private readonly tenantNameById = new Map<string, string>();

  ngOnInit(): void {
    this.crudService
      .getSimple<TenantLookupDto[]>(FreeSwitchInstanceDto, null, 'tenant-lookup')
      .subscribe({
        next: list => {
          this.tenantNameById.clear();
          (list ?? []).forEach(t => this.tenantNameById.set(t.id, t.name));
        },
      });
  }

  get popoverTenantNames(): string[] {
    const assignedTenants = this.tenantPopoverInstance?.assignedTenants;
    if (!Array.isArray(assignedTenants)) {
      return [];
    }
    return assignedTenants.map(t => this.tenantNameById.get(t.tenantId) ?? t.tenantId);
  }

  trackByIndex(index: number): number {
    return index;
  }

  ngAfterViewInit(): void {
    const l = (key: string) => this.localization.instant(key);

    const columns: BaseListColumn<FreeSwitchInstanceDto>[] = [
      { field: 'name', header: l('FreeswitchIntegration::InstanceName'), bodyTemplate: this.nameTpl },
      { field: 'ipAddress', header: l('FreeswitchIntegration::IpAddress') },
      { field: 'assignedTenants', header: l('FreeswitchIntegration::AssignedTenants'), bodyTemplate: this.tenantsTpl },
      { field: 'id', header: l('FreeswitchIntegration::Actions'), width: '140px', bodyTemplate: this.actionsTpl },
    ];

    this.listConfig = {
      columns,
      trackByField: 'id',
      emptyMessage: l('FreeswitchIntegration::NoFreeSwitchInstancesFound'),
    };

    this.cdr.detectChanges();
  }

  getMenuItems(row: FreeSwitchInstanceDto): ActionMenuItem[] {
    return [
      {
        label: this.localization.instant('FreeswitchIntegration::EditFreeSwitchInstance'),
        icon: 'pi pi-pencil',
        action: () => this.edit.emit(row),
      },
      {
        label: this.localization.instant('FreeswitchIntegration::Console'),
        icon: 'pi pi-desktop',
        visible: this.isHostAdmin,
        action: () => this.console.emit(row),
      },
      {
        label: this.localization.instant('FreeswitchIntegration::Delete'),
        icon: 'pi pi-trash',
        severity: 'danger',
        visible: this.canDelete,
        action: () => this.delete.emit(row),
      },
    ];
  }

  showTenantPopover(instance: FreeSwitchInstanceDto, event: Event): void {
    event.stopPropagation();
    this.tenantPopoverInstance = instance;
    this.tenantPopoverRef.toggle(event);
  }
}
